import { useEffect, useRef } from "react";
import { onWorkspaceChange } from "@/contexts/AuthContext";

/**
 * Hook that automatically calls a refresh function when the active workspace changes.
 * 
 * The refreshFn is called:
 * - On mount (initial load)
 * - Whenever the workspace changes (user switches workspace)
 * 
 * Usage:
 * ```tsx
 * useWorkspaceRefresh(async () => {
 *   const data = await fetchData();
 *   setData(data);
 * });
 * ```
 */
export function useWorkspaceRefresh(refreshFn: () => Promise<void>) {
  // Keep a stable ref so the workspace-change listener always calls the latest version
  // without needing to re-subscribe every time the callback identity changes.
  const refreshRef = useRef(refreshFn);
  refreshRef.current = refreshFn;

  // Initial load on mount — runs once, no deps needed because we use the ref.
  useEffect(() => {
    refreshRef.current().catch(console.error);
  // eslint-disable-next-line react-hooks/exhaustive-deps
  }, []);

  // Re-run whenever workspace changes.
  useEffect(() => {
    const unsubscribe = onWorkspaceChange(() => {
      refreshRef.current().catch(console.error);
    });
    return unsubscribe;
  }, []); // stable — never re-subscribes
}
